import React from 'react';
import { Tag, theme as antTheme, Tooltip } from 'antd';
import dayjs from 'dayjs';

import { Stakeholder } from '@totem/components/accountPlan/types';
import colors from '@totem/styles/colors';

import './accountPlan.css';

export const money = (value: number) => {
  if (value >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(1)}M`;
  }
  if (value >= 10_000) {
    return `$${(value / 1_000).toFixed(0)}K`;
  }
  return value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
};

export const moneyExact = (value: number) =>
  value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });

export const shortDate = (value?: string | null) =>
  value ? dayjs(value).format("MMM D 'YY") : '—';

export const bandColor = (band: string) => {
  switch (band) {
    case 'healthy':
      return 'green';
    case 'watch':
      return 'orange';
    default:
      return 'red';
  }
};

export const bandLabel = (band: string) => {
  switch (band) {
    case 'healthy':
      return 'healthy';
    case 'watch':
      return 'watch';
    default:
      return 'at risk';
  }
};

export const priorityColor = (priority: string) => {
  switch (priority) {
    case 'P1':
    case 'high':
      return 'red';
    case 'P2':
    case 'medium':
      return 'orange';
    default:
      return 'default';
  }
};

export const STAGE_LABELS: Record<string, string> = {
  qualifying: 'Qualifying',
  proposal: 'Proposal',
  commit: 'Commit',
  onHold: 'On hold',
  won: 'Won',
  lost: 'Lost',
};

export const STAGE_COLORS: Record<string, string> = {
  qualifying: 'default',
  proposal: 'blue',
  commit: 'green',
  onHold: 'orange',
  won: 'green',
  lost: 'red',
};

export const STATUS_LABELS: Record<string, string> = {
  notStarted: 'Not Started',
  inProgress: 'In Progress',
  onHold: 'On Hold',
  complete: 'Complete',
};

export const STATUS_COLORS: Record<string, string> = {
  notStarted: 'default',
  inProgress: 'blue',
  onHold: 'orange',
  complete: 'green',
};

export const PRIORITY_LABELS: Record<string, string> = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

export const TIERS = [
  'Tier 1 — Strategic',
  'Tier 2 — Growth',
  'Tier 3 — Standard',
  'New Logo',
];

export const RELATIONSHIPS = [
  'Champion',
  'Supporter',
  'Neutral',
  'Detractor',
  'Unknown',
];

export const DECISION_ROLES = [
  'Economic buyer',
  'Technical buyer',
  'User',
  'Influencer',
  'Gatekeeper',
];

export const newId = () =>
  `${Date.now().toString(16)}${Math.random().toString(16).slice(2, 10)}`;

export const RELATIONSHIP_COLORS: Record<string, string> = {
  Champion: 'gold',
  Supporter: 'green',
  Neutral: 'default',
  Detractor: 'red',
  Unknown: 'default',
};

export const SENTIMENT: Record<string, string> = {
  positive: '😊',
  neutral: '😐',
  negative: '☹️',
};

export const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');

// Ids of everyone who reports to the stakeholder, directly or indirectly.
export const subordinateIds = (
  stakeholders: Stakeholder[],
  managerId: string,
) => {
  const found = new Set<string>();
  const pending = [managerId];
  while (pending.length > 0) {
    const current = pending.pop();
    stakeholders
      .filter(
        (stakeholder) =>
          stakeholder.reportsTo === current && !found.has(stakeholder.id),
      )
      .forEach((stakeholder) => {
        found.add(stakeholder.id);

        pending.push(stakeholder.id);
      });
  }
  return found;
};

// "Live from Portal" sections are read-only; "Maintained by AM" sections are edited here.
const LIVE_BLUE = '#1d4ed8';
// Solid light pill backgrounds stay readable on the navy card headers and in light or dark themes.

type PillProps = {
  tip?: string;
  onClick?: () => void;
};

export const LiveTag = ({ tip }: PillProps) => (
  <Tooltip title={tip ?? 'Auto-synced from the IB Portal (read-only)'}>
    <span styleName="pill" style={{ background: '#e0eaff', color: LIVE_BLUE }}>
      <span styleName="pill-dot" style={{ background: LIVE_BLUE }} />
      Live from Portal
    </span>
  </Tooltip>
);

export const ManualTag = ({ onClick }: PillProps) => (
  <Tooltip title="Maintained by the account team — click to edit">
    <span
      styleName="pill"
      role={onClick ? 'button' : undefined}
      style={{
        background: '#fde7d8',
        color: colors.branding.accent,
        cursor: onClick ? 'pointer' : 'default',
      }}
      onClick={onClick}
    >
      <span
        styleName="pill-dot"
        style={{ background: colors.branding.accent }}
      />
      Maintained by AM ✎
    </span>
  </Tooltip>
);

// Solid priority badge (P1 red, P2 amber) used in worklists and the action plan.
export const PriorityBadge = ({
  priority,
  label,
}: {
  priority: string;
  label?: string;
}) => {
  const color = priorityColor(priority);
  const background = { red: '#dc2626', orange: '#f59e0b' }[color];
  return background ? (
    <Tag color={background} style={{ fontWeight: 700, color: '#ffffff' }}>
      {label ?? priority}
    </Tag>
  ) : (
    <Tag style={{ fontWeight: 700 }}>{label ?? priority}</Tag>
  );
};

// Outlined stat box: big value over a small uppercase label (and optional muted meta line).
export const StatBox = ({
  label,
  value,
  meta,
  alert,
  labelFirst,
}: {
  label: string;
  value: React.ReactNode;
  meta?: React.ReactNode;
  alert?: boolean;
  labelFirst?: boolean;
}) => {
  const { token } = antTheme.useToken();
  const labelNode = (
    <div styleName="stat-label" style={{ color: token.colorTextSecondary }}>
      {label}
    </div>
  );
  return (
    <div
      style={{
        height: '100%',
        padding: '12px 14px',
        border: `1px solid ${token.colorBorderSecondary}`,
        borderRadius: token.borderRadiusLG,
        background: token.colorBgContainer,
      }}
    >
      {labelFirst && labelNode}
      <div
        style={{
          fontSize: labelFirst ? 28 : 22,
          fontWeight: 700,
          lineHeight: 1.2,
          color: alert ? token.colorError : token.colorTextHeading,
        }}
      >
        {value}
      </div>
      {!labelFirst && labelNode}
      {meta && (
        <div
          style={{
            marginTop: 4,
            fontSize: 12,
            color: token.colorTextSecondary,
          }}
        >
          {meta}
        </div>
      )}
    </div>
  );
};

export const SectionHeading = ({
  children,
  inCard,
}: {
  children: React.ReactNode;
  // inside a card: no top margin
  inCard?: boolean;
}) => {
  const { token } = antTheme.useToken();
  return (
    <span
      styleName="section-heading"
      style={{
        color: token.colorTextSecondary,
        ...(inCard ? { margin: '0 0 8px' } : {}),
      }}
    >
      {children}
    </span>
  );
};

// Program colors used across the Revenue View (orange = Optimize, navy = network operations...).
export const programColor = (program: string) => {
  const name = program.toLowerCase();
  if (name.includes('optimize')) {
    return colors.branding.accent;
  }
  if (name.includes('network') || name.includes('soc')) {
    return colors.branding.primaryDark;
  }
  if (name.includes('inventory') || name.includes('threat')) {
    return '#3b82f6';
  }
  if (name.includes('remote')) {
    return '#64748b';
  }
  return '#c49a6c';
};

export const ACTIVITY_COLORS: Record<string, string> = {
  live: 'green',
  starting: 'green',
  renewed: 'green',
  inProgress: 'gold',
  stalled: 'orange',
  verify: 'red',
  lost: 'red',
};

export const RENEWAL_COLORS: Record<string, string> = {
  contracted: 'green',
  start: 'blue',
  negotiate: 'gold',
  decision: 'red',
  ends: 'default',
};
